import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

const LocationSelector = ({ onLocationSelect, forceShow = false }) => {
  const [showModal, setShowModal] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState('');
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(true);

  useEffect(() => {
    fetchCities();
    const savedCity = localStorage.getItem('selectedCity');
    if (savedCity && !forceShow) {
      setSelectedCity(savedCity);
      if (onLocationSelect) onLocationSelect(savedCity);
      setShowModal(false);
    }
  }, [onLocationSelect, forceShow]);

  const fetchCities = async () => {
    try {
      // Try API first
      const response = await fetch(`${API_BASE_URL}/api/public/cities`);
      const data = await response.json();
      if (data.success) {
        setCities(data.cities.map(city => ({
          id: city.toLowerCase(),
          name: city.charAt(0).toUpperCase() + city.slice(1),
          state: 'Odisha'
        })));
        return;
      }
    } catch (error) {
      console.log('API not available, fetching from database directly...');
    }
    
    // Fetch directly from database if API fails
    try {
      const ticketsResponse = await fetch(`${API_BASE_URL}/api/public/test-tickets`);
      const ticketsData = await ticketsResponse.json();
      if (ticketsData.success) {
        const uniqueCities = [...new Set(ticketsData.tickets.map(t => t.city))].sort();
        setCities(uniqueCities.map(city => ({
          id: city.toLowerCase(),
          name: city.charAt(0).toUpperCase() + city.slice(1),
          state: 'Odisha'
        })));
      }
    } catch (dbError) {
      console.error('Error fetching cities from database:', dbError);
    } finally {
      setLoadingCities(false);
    }
  };

  const detectLocation = () => {
    setLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            const response = await fetch(
              `https://api.opencagedata.com/geocode/v1/json?q=${latitude}+${longitude}&key=demo_key`
            );
            const data = await response.json();
            const city = data.results[0]?.components?.city || data.results[0]?.components?.town;
            
            const matchedCity = cities.find(c => 
              c.name.toLowerCase().includes(city?.toLowerCase() || '')
            );
            
            if (matchedCity) {
              handleCitySelect(matchedCity.id);
            } else {
              setLoading(false);
              alert('Service not available in your area. Please select from available cities.');
            }
          } catch (error) {
            setLoading(false);
            alert('Unable to detect location. Please select manually.');
          }
        },
        () => {
          setLoading(false);
          alert('Location access denied. Please select your city manually.');
        }
      );
    } else {
      setLoading(false);
      alert('Geolocation not supported. Please select your city manually.');
    }
  };

  const handleCitySelect = (cityId) => {
    setSelectedCity(cityId);
    localStorage.setItem('selectedCity', cityId);
    if (onLocationSelect) onLocationSelect(cityId);
    setShowModal(false);
  };

  if (!showModal) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '40px',
        maxWidth: '500px',
        width: '90%',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '20px' }}>📍</div>
        <h2 style={{ marginBottom: '16px', color: '#2d3748' }}>Choose Your Location</h2>
        <p style={{ color: '#718096', marginBottom: '30px' }}>
          We need your location to show you the best experiences in your city
        </p>

        <button
          onClick={detectLocation}
          disabled={loading}
          style={{
            width: '100%',
            padding: '16px',
            background: 'linear-gradient(45deg, #4facfe, #00f2fe)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '20px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? '🔄 Detecting Location...' : '📍 Use My Current Location'}
        </button>

        <div style={{ margin: '20px 0', color: '#a0aec0' }}>OR</div>

        <div style={{ textAlign: 'left' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#4a5568' }}>
            Select Your City
          </label>
          <select
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
            disabled={loadingCities}
            style={{
              width: '100%',
              padding: '12px',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '16px',
              marginBottom: '20px',
              opacity: loadingCities ? 0.7 : 1
            }}
          >
            <option value="">{loadingCities ? 'Loading cities...' : 'Choose a city...'}</option>
            {cities.map(city => (
              <option key={city.id} value={city.id}>
                {city.name}, {city.state}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={() => selectedCity && handleCitySelect(selectedCity)}
          disabled={!selectedCity}
          style={{
            width: '100%',
            padding: '16px',
            background: selectedCity ? 'linear-gradient(45deg, #ff6b6b, #feca57)' : '#e2e8f0',
            color: selectedCity ? 'white' : '#a0aec0',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: selectedCity ? 'pointer' : 'not-allowed'
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default LocationSelector;